import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";
import { computeCourierMeasures } from "../../utils/courierControlPure";
import { searchMuestrasCourier } from "../../api/CourierControl/courierControlApi";

export const emptyLine = (partial = {}) => ({
  Temporada: "",
  Marca: "",
  Estilo: "",
  TipoPrenda: "",
  PesoKg: "",
  Tejido: "",
  Color: "",
  Talla: "",
  TipoMuestra: "",
  CodigoMuestra: "",
  IdMuestra: null,
  Motivo: "",
  ...partial,
});

export const emptyEnvio = (partial = {}) => ({
  Direccion: "SALIDA",
  Ambito: "NACIONAL",
  Fecha: new Date().toISOString().slice(0, 10),
  SolicitadoPor: "",
  Origen: "",
  Destino: "",
  Contacto: "",
  TipoEmbalaje: "",
  LargoCm: "",
  AnchoCm: "",
  AltoCm: "",
  PesoBrutoKg: "",
  CompaniaCourier: "",
  NumeroAwb: "",
  Coste: "",
  CosteReal: "",
  Pais: "",
  Estado: "PEDIDO",
  Notas: "",
  lineas: [emptyLine()],
  ...partial,
});

const fmt = (value, digits = 3) => {
  if (value == null || value === "") return "—";
  return Number(value).toLocaleString("es-ES", {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  });
};

function SampleCodeField({ value, onChange }) {
  const { t } = useTranslation();
  const [options, setOptions] = useState([]);

  useEffect(() => {
    const q = String(value.CodigoMuestra || "").trim();
    if (q.length < 2) {
      setOptions([]);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const rows = await searchMuestrasCourier(q);
        if (!cancelled) setOptions(Array.isArray(rows) ? rows : []);
      } catch {
        if (!cancelled) setOptions([]);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [value.CodigoMuestra]);

  return (
    <Autocomplete
      freeSolo
      size="small"
      options={options}
      filterOptions={(opts) => opts}
      getOptionLabel={(option) =>
        typeof option === "string" ? option : option.MUESTRA || ""
      }
      inputValue={value.CodigoMuestra || ""}
      onInputChange={(_, next, reason) => {
        if (reason === "reset") return;
        onChange({
          ...value,
          CodigoMuestra: next,
          IdMuestra: null,
        });
      }}
      onChange={(_, next) => {
        if (next && typeof next === "object") {
          onChange({
            ...value,
            CodigoMuestra: next.MUESTRA || "",
            IdMuestra: next.ID_MUESTRA,
          });
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={t("courierControl.sampleCode")}
          helperText={
            value.IdMuestra
              ? t("courierControl.linkedSample")
              : t("courierControl.sampleHint")
          }
        />
      )}
    />
  );
}

export default function CourierControlDialog({
  open,
  envio,
  saving,
  onClose,
  onChange,
  onSave,
  onDelete,
}) {
  const { t } = useTranslation();
  const measures = useMemo(
    () =>
      computeCourierMeasures({
        largoCm: envio.LargoCm,
        anchoCm: envio.AnchoCm,
        altoCm: envio.AltoCm,
        pesoBrutoKg: envio.PesoBrutoKg,
        coste: envio.Coste,
        costeReal: envio.CosteReal,
      }),
    [envio],
  );

  const setField = (field, value) => onChange({ ...envio, [field]: value });

  const setLine = (index, next) => {
    const lineas = envio.lineas.map((linea, i) => (i === index ? next : linea));
    onChange({ ...envio, lineas });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 800,
          py: 2,
          background: (theme) =>
            theme.palette.mode === "dark"
              ? undefined
              : "linear-gradient(145deg, #e8f6f8 0%, #f4f0ff 55%, #fff 100%)",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        {envio.IdCourierControl
          ? t("courierControl.editTitle")
          : t("courierControl.newRequest")}
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 0.5, fontWeight: 400 }}
        >
          {t("courierControl.anythingHint")}
        </Typography>
      </DialogTitle>
      <DialogContent dividers sx={{ pt: 2.5 }}>
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={4} md={3}>
            <TextField
              select
              fullWidth
              size="small"
              label={t("courierControl.direction")}
              value={envio.Direccion}
              onChange={(e) => setField("Direccion", e.target.value)}
            >
              <MenuItem value="SALIDA">{t("courierControl.exit")}</MenuItem>
              <MenuItem value="ENTRADA">{t("courierControl.entry")}</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <TextField
              select
              fullWidth
              size="small"
              label={t("courierControl.scope")}
              value={envio.Ambito}
              onChange={(e) => setField("Ambito", e.target.value)}
            >
              <MenuItem value="NACIONAL">
                {t("courierControl.national")}
              </MenuItem>
              <MenuItem value="INTERNACIONAL">
                {t("courierControl.international")}
              </MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label={t("courierControl.date")}
              InputLabelProps={{ shrink: true }}
              value={String(envio.Fecha || "").slice(0, 10)}
              onChange={(e) => setField("Fecha", e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <TextField
              select
              fullWidth
              size="small"
              label={t("courierControl.status")}
              value={envio.Estado || "PEDIDO"}
              onChange={(e) => setField("Estado", e.target.value)}
            >
              <MenuItem value="PEDIDO">
                {t("courierControl.statusPedido")}
              </MenuItem>
              <MenuItem value="EN_CURSO">
                {t("courierControl.statusEnCurso")}
              </MenuItem>
              <MenuItem value="ENTREGADO">
                {t("courierControl.statusEntregado")}
              </MenuItem>
              <MenuItem value="CANCELADO">
                {t("courierControl.statusCancelado")}
              </MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label={t("courierControl.requestedBy")}
              value={envio.SolicitadoPor || ""}
              onChange={(e) => setField("SolicitadoPor", e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label={t("courierControl.origin")}
              value={envio.Origen || ""}
              onChange={(e) => setField("Origen", e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label={t("courierControl.destination")}
              value={envio.Destino || ""}
              onChange={(e) => setField("Destino", e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label={t("courierControl.contact")}
              value={envio.Contacto || ""}
              onChange={(e) => setField("Contacto", e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              size="small"
              label={t("courierControl.packing")}
              value={envio.TipoEmbalaje || ""}
              onChange={(e) => setField("TipoEmbalaje", e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              size="small"
              label={t("courierControl.country")}
              value={envio.Pais || ""}
              onChange={(e) => setField("Pais", e.target.value)}
            />
          </Grid>

          <Grid item xs={4} sm={2}>
            <TextField
              fullWidth
              size="small"
              label={t("courierControl.length")}
              value={envio.LargoCm ?? ""}
              onChange={(e) => setField("LargoCm", e.target.value)}
            />
          </Grid>
          <Grid item xs={4} sm={2}>
            <TextField
              fullWidth
              size="small"
              label={t("courierControl.width")}
              value={envio.AnchoCm ?? ""}
              onChange={(e) => setField("AnchoCm", e.target.value)}
            />
          </Grid>
          <Grid item xs={4} sm={2}>
            <TextField
              fullWidth
              size="small"
              label={t("courierControl.height")}
              value={envio.AltoCm ?? ""}
              onChange={(e) => setField("AltoCm", e.target.value)}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              fullWidth
              size="small"
              label={t("courierControl.grossWeight")}
              value={envio.PesoBrutoKg ?? ""}
              onChange={(e) => setField("PesoBrutoKg", e.target.value)}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              fullWidth
              size="small"
              label={t("courierControl.courier")}
              value={envio.CompaniaCourier || ""}
              onChange={(e) => setField("CompaniaCourier", e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label={t("courierControl.awb")}
              value={envio.NumeroAwb || ""}
              onChange={(e) => setField("NumeroAwb", e.target.value)}
            />
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField
              fullWidth
              size="small"
              label={t("courierControl.cost")}
              value={envio.Coste ?? ""}
              onChange={(e) => setField("Coste", e.target.value)}
            />
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField
              fullWidth
              size="small"
              label={t("courierControl.realCost")}
              value={envio.CosteReal ?? ""}
              onChange={(e) => setField("CosteReal", e.target.value)}
            />
          </Grid>

          <Grid item xs={12}>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
                px: 1.5,
                py: 1.25,
                borderRadius: 1,
                bgcolor: "action.hover",
              }}
            >
              <Typography variant="body2">
                {t("courierControl.volume")}:{" "}
                <b>{fmt(measures.volumenM3, 6)} m³</b>
              </Typography>
              <Typography variant="body2">
                {t("courierControl.volWeight")}:{" "}
                <b>{fmt(measures.pesoVolumetricoKg)} kg</b>
              </Typography>
              <Typography variant="body2">
                {t("courierControl.chargeable")}:{" "}
                <b>{fmt(measures.pesoFacturableKg)} kg</b>
              </Typography>
              <Typography variant="body2">
                {t("courierControl.difference")}:{" "}
                <b>
                  {measures.diferencia == null
                    ? "—"
                    : `${fmt(measures.diferencia, 2)} €`}
                </b>
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              {t("courierControl.measuresHint")}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={2}
              label={t("courierControl.notes")}
              value={envio.Notas || ""}
              onChange={(e) => setField("Notas", e.target.value)}
            />
          </Grid>
        </Grid>

        <Box sx={{ display: "flex", alignItems: "center", mt: 2.5, mb: 1 }}>
          <Typography sx={{ fontWeight: 700, flex: 1 }}>
            {t("courierControl.lines")}
          </Typography>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() =>
              onChange({ ...envio, lineas: [...envio.lineas, emptyLine()] })
            }
          >
            {t("courierControl.addLine")}
          </Button>
        </Box>

        {envio.lineas.map((linea, index) => (
          <Box
            key={`line-${index}`}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              p: 1.25,
              mb: 1.25,
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <IconButton
                size="small"
                aria-label={t("courierControl.deleteLine")}
                onClick={() =>
                  onChange({
                    ...envio,
                    lineas:
                      envio.lineas.length === 1
                        ? [emptyLine()]
                        : envio.lineas.filter((_, i) => i !== index),
                  })
                }
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
            <Grid container spacing={1.25}>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  label={t("courierControl.season")}
                  value={linea.Temporada || ""}
                  onChange={(e) =>
                    setLine(index, { ...linea, Temporada: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  label={t("courierControl.brand")}
                  value={linea.Marca || ""}
                  onChange={(e) =>
                    setLine(index, { ...linea, Marca: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label={t("courierControl.style")}
                  value={linea.Estilo || ""}
                  onChange={(e) =>
                    setLine(index, { ...linea, Estilo: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  label={t("courierControl.garmentType")}
                  value={linea.TipoPrenda || ""}
                  onChange={(e) =>
                    setLine(index, { ...linea, TipoPrenda: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  label={t("courierControl.fabric")}
                  value={linea.Tejido || ""}
                  onChange={(e) =>
                    setLine(index, { ...linea, Tejido: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={4} sm={2}>
                <TextField
                  fullWidth
                  size="small"
                  label={t("courierControl.color")}
                  value={linea.Color || ""}
                  onChange={(e) =>
                    setLine(index, { ...linea, Color: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={4} sm={2}>
                <TextField
                  fullWidth
                  size="small"
                  label={t("courierControl.size")}
                  value={linea.Talla || ""}
                  onChange={(e) =>
                    setLine(index, { ...linea, Talla: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={4} sm={2}>
                <TextField
                  fullWidth
                  size="small"
                  label={t("courierControl.lineWeight")}
                  value={linea.PesoKg ?? ""}
                  onChange={(e) =>
                    setLine(index, { ...linea, PesoKg: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  label={t("courierControl.sampleType")}
                  value={linea.TipoMuestra || ""}
                  onChange={(e) =>
                    setLine(index, { ...linea, TipoMuestra: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  label={t("courierControl.reason")}
                  value={linea.Motivo || ""}
                  onChange={(e) =>
                    setLine(index, { ...linea, Motivo: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <SampleCodeField
                  value={linea}
                  onChange={(next) => setLine(index, next)}
                />
              </Grid>
            </Grid>
          </Box>
        ))}
      </DialogContent>
      <DialogActions
        sx={{ px: 2.5, py: 1.75, justifyContent: "space-between" }}
      >
        <Box>
          {envio.IdCourierControl ? (
            <Button
              color="error"
              onClick={onDelete}
              disabled={saving}
              sx={{ borderRadius: 2, fontWeight: 700, textTransform: "none" }}
            >
              {t("courierControl.delete")}
            </Button>
          ) : null}
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            onClick={onClose}
            disabled={saving}
            sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
          >
            {t("courierControl.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={onSave}
            disabled={saving}
            sx={{
              borderRadius: 2,
              fontWeight: 800,
              textTransform: "none",
              px: 2.5,
            }}
          >
            {t("courierControl.save")}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
